// The help/explanation contract — one shared shape that BOTH the inline ⓘ popovers
// and the Methods & Limitations page render from, so an explanation can never drift
// from itself (and, kept next to the code it describes, from the computation).
//
// Every Explainer must state a `limitation`. That is deliberate: SeaState is a
// go/no-go decision aid, and for a decision aid the honest statement of what a
// number CAN'T tell you is as load-bearing as the number itself.

export type HelpCategory =
  | "foundations" // cross-cutting framing: models vs reality, horizon, uncertainty, units
  | "forecast" // the meteogram layers
  | "workability" // the go/no-go read and its limits
  | "svp" // sound-velocity profiles
  | "operations" // per-operation context panels (SEEP, EBS)
  | "map" // basemap + overlays
  | "fleet" // AIS
  | "vessels"; // fleet registry / speeds

// Ordered for the Methods page: foundations first (they frame everything), then the
// products. `label` is the on-page section heading.
export const HELP_CATEGORIES: { key: HelpCategory; label: string }[] = [
  { key: "foundations", label: "Reading the data honestly" },
  { key: "forecast", label: "Wind, wave & the meteogram" },
  { key: "workability", label: "Workability & limits" },
  { key: "svp", label: "Sound-velocity profiles" },
  { key: "operations", label: "Operation context — seep hunting & baseline surveys" },
  { key: "map", label: "Map & overlays" },
  { key: "fleet", label: "Fleet tracking (AIS)" },
  { key: "vessels", label: "Vessels & speeds" },
];

export interface Explainer {
  /** Display name — the popover/section heading (e.g. "GEFS spread"). */
  term: string;
  category: HelpCategory;
  /** 1–2 plain sentences for the inline popover. */
  short: string;
  /** Paragraphs for the Methods page (the full treatment). */
  full: string[];
  /** e.g. "knots", "metres", "°C". Omit when not a measured quantity. */
  units?: string;
  /** Provenance — the source/model this comes from. */
  source?: string;
  /** The key caveat. REQUIRED — every explainer names a limitation. */
  limitation: string;
  /** Sibling keys worth linking from the popover / page section. */
  related?: HelpKey[];
  /** Stable id for the Methods page `id=` and the popover deep-link `/methods#<anchor>`. */
  anchor: string;
}

// The master key list — the single inventory that drives the registry (content.ts),
// the Methods page (iterates it by category), and the inline affordances (attachHelp).
// A total `Record<HelpKey, Explainer>` in content.ts forces every key to be explained.
export type HelpKey =
  // foundations
  | "fnd.models"
  | "fnd.horizon"
  | "fnd.ensemble"
  | "fnd.provenance"
  | "fnd.units"
  | "fnd.notForNav"
  | "fnd.predictability"
  // forecast
  | "fc.wind"
  | "fc.gefs"
  | "fc.squall"
  | "fc.wave"
  | "fc.period"
  | "fc.seaChar"
  | "fc.current"
  | "fc.daylight"
  | "fc.visibility"
  | "fc.bathymetry"
  | "fc.buoy"
  | "fc.table"
  // workability
  | "wk.verdict"
  | "wk.limits"
  | "wk.default"
  | "wk.briefing"
  | "wk.limitColour"
  // svp
  | "svp.overview"
  | "svp.soundSpeed"
  | "svp.thermocline"
  | "svp.mld"
  | "svp.sofar"
  // operation context
  | "ops.seep"
  | "ops.ebs"
  // map
  | "map.basemap"
  | "map.eez"
  | "map.ports"
  | "map.buoys"
  | "map.jurisdiction"
  | "map.tropical"
  | "map.rings"
  | "map.socks"
  // fleet
  | "fleet.ais"
  // vessels
  | "vsl.speed";
